import Gigya, { BaseParams, StringList } from './gigya.js'
import { AccountsB2bAuth } from './accounts.b2b.auth.js'
import { AccountsB2bSetup } from './accounts.b2b.setup.js'
import { AccountsB2bWebhooks } from './accounts.b2b.webhooks.js'
import GigyaResponse from './interfaces/gigya-response.js'
import Profile from './interfaces/profile.js'

export type CreateBusinessEntityParams = {
	businessModelId: string
	businessEntity: Record<string, any>
}
export type DeleteBusinessEntityParams = {
	businessModelId: string
	searchKey: string
}
export type GetBusinessEntityParams = DeleteBusinessEntityParams
export type UpdateBusinessEntityParams = CreateBusinessEntityParams

export type CreateBusinessEntityTypeParams = {
	name: string
	description?: string
	attributes?: {
		assignable: boolean
		required: boolean
		readOnly: boolean
		onlyOnCreate: boolean
		type: 'string' | 'number'
		maxLength: number
		defaultValue: string[]
		order: number
		displayAttribute: {
			required: boolean
			readOnly: boolean
			onlyOnCreate: boolean
			type: 'string' | 'number'
			maxLength: number
			defaultValue: string[]
		}
	}[]
	logoUrl?: string
	displayName?: string
	rulesets?: {
		name: string
		description: string
		rules: {
			attribute: string
			operator: 'EQUAL' | 'NOT_EQUAL'
			value: string
		}[]
	}[]
	context?: Record<string, any>
}
export type DeleteBusinessEntityTypeParams = {
	id: string
	context: Record<string, any>
}
export type GetBusinessEntityTypeParams = DeleteBusinessEntityTypeParams
export type UpdateBusinessEntityTypeParams = CreateBusinessEntityTypeParams & {
	id: string
}

export type CreateBusinessEntityCatalogParams = {
	name: string
	description?: string
	businessEntityTypeId: string
	rulesetNames?: string[]
	orgSchemaId: string
	orgAvailabilityType: string
	organizationBPIDs: string[]
	availabilityRules: {
		operator: 'EQUALS' | 'NOT_EQUALS'
		attribute: string
		value: string
	}[]
	context: Record<string, any>
}
export type DeleteBusinessEntityCatalogParams = {
	id: string
	context: Record<string, any>
}
export type GetBusinessEntityCatalogParams = DeleteBusinessEntityCatalogParams
export type UpdateBusinessEntityCatalogParams = CreateBusinessEntityCatalogParams & {
	id: string
}

export type CreateBusinessModelParams = {
	primaryBusinessModelId: string
	businessModelId: string
	displaySettings: {
		displayName: string
		logoURL: string
	}
	organizationGroupKey?: {
		[organizationAttribute: string]: string[]
	}
	order: string[]
	displayable: boolean
}
export type DeleteBusinessModelParams = {
	businessModelId: string
}
export type GetBusinessModelParams = DeleteBusinessModelParams
export type UpdateBusinessModelParams = Pick<
	CreateBusinessModelParams,
	'businessModelId' | 'displaySettings' | 'order'
>
export type GetBusinessModelResponse = {
	organizationGroupKey: CreateBusinessModelParams['organizationGroupKey']
}
export type UpdateBusinessModelResponse = {
	businessModelId: string
}

export type CreateTargetAudienceParams = {}
export type DeleteTargetAudienceParams = {}
export type GetTargetAudienceParams = {}
export type UpdateTargetAudienceParams = {}

export type GetOrganizationInfoParams = {
	bpid: string
}
export type GetOrganizationInfoResponse = Omit<OrganizationGeneral, 'name'> & {
	orgId: string
	orgName: string
	bpid: string
	description: string
	source: 'SELF_REGISTRATION' | 'BACK_OFFICE' | 'IT_ADMIN' | 'IMPORT'
	type: OrganizationGeneral['type']
	status: Exclude<RegisterOrganizationParams['status'], undefined>
}

export type SetOrganizationInfoParams<TOrgAddress = OrganizationAddress, TReq = Requester> = Omit<
	OrganizationGeneral,
	'name'
> & {
	bpid: string
	orgName: string
	info?: TOrgAddress
	requester?: TReq
}

export interface OrganizationAddress {
	city?: string
	street_address?: string
	state?: string
	zip_code?: string
	country?: string
}

export interface OrganizationGeneral {
	name: string
	bpid?: string
	membersLimit?: number
	type?: 'CUSTOMER' | 'DISTRIBUTOR' | 'VENDOR' | 'SUPPLIER' | 'PARTNER'
	description?: string
}

type FieldName = string
type FieldSchema = {
	type: 'string' | 'integer'
	required: boolean
	allowNull: boolean
}

export type GetOrganizationSchemaResponse = {
	organizationSchema: {
		fields: Record<FieldName, FieldSchema>
	}
	requesterSchema: {
		fields: Record<FieldName, FieldSchema>
	}
}

export interface Requester {
	firstName: string
	lastName: string
	email: string
	phone?: string
	jobFunction?: string
	department?: string
}

export type RegisterOrganizationParams<
	TOrgGeneral = OrganizationGeneral,
	TOrgAddress = OrganizationAddress,
	TReq = Requester
> = {
	organization: TOrgGeneral & TOrgAddress
	requester: TReq
	importPolicy?: 'upsert' | 'insert'
	status?: 'draft' | 'approved' | 'suspended'
}
export type RegisterOrganizationResponse = {
	bpid?: string
}
export type DeleteOrganizationParams = {
	bpid: string
}
export type SetOrganizationBpidParams = {
	bpid: string
	newBpid: string
	context: Record<string, any>
}
export type SetOrganizationBpidResponse = {
	bpid: string
}
export type SetOrganizationWorkflowStatusParams = {
	webhookToken: string
	bpid: string
	path: string
	stepId: string
	status: 'declined' | 'approved'
}
export type GetAccountOrganizationInfoParams = {
	UID: string
	bpid: string
}

export type Policy = {
	name: string
	restricted: boolean
	autoAssignment: boolean
	active: boolean
	complete: boolean
	externalId: string
	roleId: string
	applications: [
		{
			name: string
			assets: Array<{
				name: string
				ruleSet: boolean
			}>
		}
	]
}
export type GetAccountOrganizationInfoResponse = {
	organization: {
		status: 'active' | 'inactive'
		orgId: string
		bpid: string
		orgName: string
		lastUpdated: string
		lastUpdatedTimestamp: number
		policies: Policy[]
	}
}

export type SetAccountOrganizationInfoParams = {
	UID: string
	bpid: string
	department?: string
	job?: string
	status?: MembershipStatus
	businessModels?: any
	roleIds?: StringList<string>
	roles?: StringList<string>
}

export type MembershipStatus = 'active' | 'inactive'
export type Role = 'Delegated Admin' | string

export type InviteMemberParams<TData> = {
	bpid: string
	username?: string
	email?: string
	phoneNumber?: string
	// relation data as object: needs tests, doesn't work reliably right now
	// accountOrganization?: {
	// 	department?: string
	// 	job?: string
	// 	status: MembershipStatus
	// 	roles?: Role[]
	// 	roleIds?: string[]
	// },
	// account organization relation data
	roles?: StringList<Role>
	roleIds?: StringList<string>
	department?: string
	job?: string
	// account 'data' field
	data?: TData
	profile?: Profile
	sendNotification?: boolean
	status?: MembershipStatus
}
export type InviteMemberResponse = {
	uid: string
	newUser: boolean
	firstTimePassword?: string
}
export type RemoveMemberParams = {
	uid: string
	bpid: string
	context?: any
}

export type SearchParams = {
	query: string
}

export type SearchResponse = {
	results: Array<{
		lastUpdated: string
		lastUpdatedTimestamp: number
		created: string
		createdTimestamp: number
		orgId: string
		orgName: string
		bpid: string
		description: string
		source: string
		type: OrganizationGeneral['type']
		status: RegisterOrganizationParams['status']
		info: {
			city: string[]
			email: string[]
			phone: string[]
			state: string[]
			mobile: string[]
			po_box: string[]
			country: string[]
			building: string[]
			zip_code: string[]
			street_address: string[]
		}
		workflowStatus: unknown | null
		memberLimit: number
	}>
	objectsCount: number
	totalCount: number
}

export class B2B {
	public readonly auth: AccountsB2bAuth
	public readonly setup: AccountsB2bSetup
	public readonly webhooks: AccountsB2bWebhooks

	constructor(protected gigya: Gigya) {
		this.auth = new AccountsB2bAuth(gigya)
		this.setup = new AccountsB2bSetup(gigya)
		this.webhooks = new AccountsB2bWebhooks(gigya)
	}

	// business entity
	public createBusinessEntity(params: BaseParams & CreateBusinessEntityParams) {
		return this.gigya.request('accounts.b2b.createBusinessEntity', params)
	}

	public deleteBusinessEntity(params: BaseParams & DeleteBusinessEntityParams) {
		return this.gigya.request('accounts.b2b.deleteBusinessEntity', params)
	}

	public getBusinessEntity(params: BaseParams & GetBusinessEntityParams) {
		return this.gigya.request('accounts.b2b.getBusinessEntity', params)
	}

	public updateBusinessEntity(params: BaseParams & UpdateBusinessEntityParams) {
		return this.gigya.request('accounts.b2b.updateBusinessEntity', params)
	}

	// business entity type
	public createBusinessEntityType(params: BaseParams & CreateBusinessEntityTypeParams) {
		return this.gigya.request('accounts.b2b.createBusinessEntityType', params)
	}

	public deleteBusinessEntityType(params: BaseParams & DeleteBusinessEntityTypeParams) {
		return this.gigya.request('accounts.b2b.deleteBusinessEntityType', params)
	}

	public getBusinessEntityType(params: BaseParams & GetBusinessEntityTypeParams) {
		return this.gigya.request('accounts.b2b.getBusinessEntityType', params)
	}

	public updateBusinessEntityType(params: BaseParams & UpdateBusinessEntityTypeParams) {
		return this.gigya.request('accounts.b2b.updateBusinessEntityType', params)
	}

	// business entity catalog

	public createBusinessEntityCatalogEntry(params: BaseParams & CreateBusinessEntityCatalogParams) {
		return this.gigya.request('accounts.b2b.createBusinessEntityCatalogEntry', params)
	}

	public deleteBusinessEntityCatalogEntry(params: BaseParams & DeleteBusinessEntityCatalogParams) {
		return this.gigya.request('accounts.b2b.deleteBusinessEntityCatalogEntry', params)
	}

	public getBusinessEntityCatalogEntry(params: BaseParams & GetBusinessEntityCatalogParams) {
		return this.gigya.request('accounts.b2b.getBusinessEntityCatalogEntry', params)
	}

	public updateBusinessEntityCatalogEntry(params: BaseParams & UpdateBusinessEntityCatalogParams) {
		return this.gigya.request('accounts.b2b.updateBusinessEntityCatalogEntry', params)
	}

	// business model

	public createBusinessModel(params: BaseParams & CreateBusinessModelParams) {
		return this.gigya.request('accounts.b2b.createBusinessModel', params)
	}

	public deleteBusinessModel(params: BaseParams & DeleteBusinessModelParams) {
		return this.gigya.request('accounts.b2b.deleteBusinessModel', params)
	}

	public getBusinessModel(params: BaseParams & GetBusinessModelParams) {
		return this.gigya.request<GigyaResponse & GetBusinessModelResponse>(
			'accounts.b2b.getBusinessModel',
			params
		)
	}

	public updateBusinessModel(params: BaseParams & UpdateBusinessModelParams) {
		return this.gigya.request<GigyaResponse & UpdateBusinessModelResponse>(
			'accounts.b2b.updateBusinessModel',
			params
		)
	}

	// target audience

	public createTargetAudience(params: BaseParams & CreateTargetAudienceParams) {
		return this.gigya.request('accounts.b2b.createTargetAudience', params)
	}

	public deleteTargetAudience(params: BaseParams & DeleteTargetAudienceParams) {
		return this.gigya.request('accounts.b2b.deleteTargetAudience', params)
	}

	public getTargetAudience(params: BaseParams & GetTargetAudienceParams) {
		return this.gigya.request('accounts.b2b.getTargetAudience', params)
	}

	public updateTargetAudience(params: BaseParams & UpdateTargetAudienceParams) {
		return this.gigya.request('accounts.b2b.updateTargetAudience', params)
	}

	// organization

	public getOrganizationSchema(params: BaseParams) {
		return this.gigya.request<GigyaResponse & GetOrganizationSchemaResponse>(
			'accounts.b2b.getOrganizationSchema',
			params
		)
	}

	public registerOrganization(params: BaseParams & RegisterOrganizationParams) {
		return this.gigya.request<GigyaResponse & RegisterOrganizationResponse>(
			'accounts.b2b.registerOrganization',
			params
		)
	}

	public deleteOrganization(params: BaseParams & DeleteOrganizationParams) {
		return this.gigya.request('accounts.b2b.deleteOrganization', params)
	}

	// organization info
	public getOrganizationInfo(params: BaseParams & GetOrganizationInfoParams) {
		return this.gigya.request<GigyaResponse & GetOrganizationInfoResponse>(
			'accounts.b2b.getOrganizationInfo',
			params
		)
	}

	public setOrganizationInfo(params: BaseParams & SetOrganizationInfoParams) {
		return this.gigya.request('accounts.b2b.setOrganizationInfo', params)
	}

	public setOrganizationBpid(params: BaseParams & SetOrganizationBpidParams) {
		return this.gigya.request<GigyaResponse & SetOrganizationBpidResponse>(
			'accounts.b2b.setOrganizationBpid',
			params
		)
	}

	public setOrganizationWorkflowStatus(params: BaseParams & SetOrganizationWorkflowStatusParams) {
		return this.gigya.request('accounts.b2b.setOrganizationWorkflowStatus', params)
	}

	// member / relationship between account and organization

	public inviteMember<TData>(params: BaseParams & InviteMemberParams<TData>) {
		type Key = keyof typeof params
		const keys = ['roles', 'roleIds'] satisfies Key[]

		const newParams = structuredClone(params)
		for (const key of keys) {
			const values = newParams[key]
			if (Array.isArray(values)) {
				newParams[key] = values.join(',')
			}
		}
		return this.gigya.request<GigyaResponse & InviteMemberResponse>(
			'accounts.b2b.inviteMember',
			newParams
		)
	}

	public removeMember(params: BaseParams & RemoveMemberParams) {
		return this.gigya.request('accounts.b2b.removeMember', params)
	}

	public getAccountOrganizationInfo(params: BaseParams & GetAccountOrganizationInfoParams) {
		return this.gigya.request<GigyaResponse & GetAccountOrganizationInfoResponse>(
			'accounts.b2b.getAccountOrganizationInfo',
			params
		)
	}

	public setAccountOrganizationInfo(params: BaseParams & SetAccountOrganizationInfoParams) {
		type Key = keyof typeof params
		const keys = ['roles', 'roleIds'] satisfies Key[]

		const newParams = structuredClone(params)
		for (const key of keys) {
			const values = newParams[key]
			if (Array.isArray(values)) {
				newParams[key] = values.join(',')
			}
		}

		return this.gigya.request('accounts.b2b.setAccountOrganizationInfo', newParams)
	}

	public search(params: BaseParams & SearchParams) {
		return this.gigya.request<SearchResponse>('accounts.b2b.search', params)
	}
}
